import React, { useState } from "react";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Award, Upload, Eye, Check, Loader2 } from "lucide-react";
import { useT } from "../../lib/LanguageContext";
import { api, extractApiError } from "../../lib/api";
import { toast } from "sonner";

export const CERTIFICATE_STYLES = [
  {
    id: "classic",
    titleKey: "brand.certificateStyle.classic",
    descKey: "brand.certificateStyle.classicDesc",
    borderClass: "border-[#5A6B54]",
    preview: (
      <div className="w-full h-11 rounded border-2 border-[#5A6B54] p-0.5 flex flex-col items-center justify-between bg-[#F9FAF8] relative">
        <div className="w-full h-full border border-[#5A6B54]/40 flex flex-col items-center justify-center p-0.5">
          <div className="h-0.5 w-6 bg-[#5A6B54]/80 rounded mb-0.5" />
          <div className="h-1 w-10 bg-[#354033] rounded mb-1" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#C5A059] border border-[#A27C34] flex items-center justify-center shadow-xs">
            <div className="w-1 h-1 rounded-full bg-white" />
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "modern",
    titleKey: "brand.certificateStyle.modern",
    descKey: "brand.certificateStyle.modernDesc",
    borderClass: "border-[#1E293B]",
    preview: (
      <div className="w-full h-11 rounded border border-[#334155] overflow-hidden flex bg-[#FAFAFA]">
        <div className="w-1/3 h-full bg-[#0F172A] border-r-2 border-[#2563EB] p-1 flex flex-col justify-between">
          <div className="w-3 h-1 bg-white/70 rounded" />
          <div className="w-4 h-2 bg-[#1E293B] rounded p-0.5">
            <div className="w-2 h-0.5 bg-[#2563EB] rounded" />
          </div>
        </div>
        <div className="flex-1 p-1.5 flex flex-col justify-center gap-0.5">
          <div className="h-1 w-12 bg-[#0F172A] rounded" />
          <div className="h-0.5 w-8 bg-[#2563EB] rounded" />
          <div className="h-0.5 w-10 bg-[#64748B] rounded" />
        </div>
      </div>
    ),
  },
  {
    id: "luxe",
    titleKey: "brand.certificateStyle.luxe",
    descKey: "brand.certificateStyle.luxeDesc",
    borderClass: "border-[#C5A059]",
    preview: (
      <div className="w-full h-11 rounded p-1 bg-[#F5F0E6] flex items-center justify-center">
        <div className="w-full h-full bg-white border border-[#C5A059] rounded-xs p-1 flex flex-col items-center justify-center relative shadow-xs">
          <div className="absolute top-0.5 left-0.5 w-1 h-1 rotate-45 bg-[#C5A059]" />
          <div className="absolute top-0.5 right-0.5 w-1 h-1 rotate-45 bg-[#C5A059]" />
          <div className="absolute bottom-0.5 left-0.5 w-1 h-1 rotate-45 bg-[#C5A059]" />
          <div className="absolute bottom-0.5 right-0.5 w-1 h-1 rotate-45 bg-[#C5A059]" />
          <div className="h-0.5 w-6 bg-[#C5A059] rounded mb-0.5" />
          <div className="h-1 w-10 bg-[#1E1B18] rounded mb-0.5" />
          <div className="h-0.5 w-8 bg-[#C5A059] rounded" />
        </div>
      </div>
    ),
  },
  {
    id: "academic",
    titleKey: "brand.certificateStyle.academic",
    descKey: "brand.certificateStyle.academicDesc",
    borderClass: "border-[#1E3A8A]",
    preview: (
      <div className="w-full h-11 rounded border-2 border-[#1E3A8A] overflow-hidden flex flex-col bg-white">
        <div className="w-full h-2.5 bg-[#102048] flex items-center justify-between px-1.5 border-b border-[#C5A059]">
          <div className="w-1.5 h-1 bg-[#C5A059] rounded-xs" />
          <div className="w-8 h-0.5 bg-white/80 rounded" />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-1">
          <div className="h-1 w-10 bg-[#102048] rounded mb-0.5" />
          <div className="h-0.5 w-12 bg-[#64748B] rounded mb-1" />
          <div className="w-full flex items-center justify-between px-2">
            <div className="w-3 h-0.5 bg-[#102048]/50" />
            <div className="w-1.5 h-1.5 rounded-full bg-[#102048] border border-[#C5A059]" />
            <div className="w-3 h-0.5 bg-[#102048]/50" />
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "minimal",
    titleKey: "brand.certificateStyle.minimal",
    descKey: "brand.certificateStyle.minimalDesc",
    borderClass: "border-[#27272A]",
    preview: (
      <div className="w-full h-11 rounded bg-white p-1 flex flex-col justify-between relative border border-dashed border-[#E4E4E7]">
        <div className="absolute top-0.5 left-0.5 text-[6px] text-[#71717A] leading-none font-mono">+</div>
        <div className="absolute top-0.5 right-0.5 text-[6px] text-[#71717A] leading-none font-mono">+</div>
        <div className="absolute bottom-0.5 left-0.5 text-[6px] text-[#71717A] leading-none font-mono">+</div>
        <div className="absolute bottom-0.5 right-0.5 text-[6px] text-[#71717A] leading-none font-mono">+</div>
        <div className="pl-2 pt-0.5">
          <div className="h-1 w-8 bg-[#18181B] rounded mb-0.5" />
          <div className="h-0.5 w-5 bg-[#71717A] rounded" />
        </div>
        <div className="border-t border-[#E4E4E7] pt-0.5 px-2 flex justify-between">
          <div className="w-2.5 h-0.5 bg-[#A1A1AA] rounded" />
          <div className="w-2.5 h-0.5 bg-[#A1A1AA] rounded" />
          <div className="w-2.5 h-0.5 bg-[#15803D] rounded" />
        </div>
      </div>
    ),
  },
];

export default function CertificateCard({ branding, setBranding, onUpload, tenant }) {
  const t = useT("admin");
  const tc = useT("common");
  const [previewing, setPreviewing] = useState(false);

  const selectedStyle = branding.certificate_style || "classic";
  const customLogoUrl = branding.certificate_logo_url || "";
  const brandLogoUrl = branding.logo_url || "";

  const handleStyleSelect = (styleId) => {
    setBranding({ ...branding, certificate_style: styleId });
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const resp = await api.get("/tenant/certificate/preview.pdf", {
        params: {
          style: selectedStyle,
          logo_url: customLogoUrl || brandLogoUrl || undefined,
        },
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
    } catch (e) {
      toast.error(extractApiError(e, tc("errors.loadFailed"), tc));
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div
      data-testid="certificate-card"
      className="bg-white rounded-2xl border border-eh-border-soft p-6 space-y-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl flex items-center gap-2 text-eh-ink">
            <Award className="w-5 h-5 text-sol-gold" />
            {t("brand.certificateTitle")}
          </h2>
          <p className="text-xs text-eh-muted mt-1">{t("brand.certificateSubtitle")}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handlePreview}
          disabled={previewing}
          data-testid="certificate-preview-btn"
          className="text-xs flex items-center gap-1.5"
        >
          {previewing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Eye className="w-3.5 h-3.5" />
          )}
          {t("brand.certificatePreviewBtn")}
        </Button>
      </div>

      {/* Selectable Design Styles */}
      <div className="space-y-3">
        <Label className="text-xs font-semibold uppercase tracking-wider text-eh-muted">
          {t("brand.certificateStyleLabel")}
        </Label>
        <div
          data-testid="certificate-style-select"
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3"
        >
          {CERTIFICATE_STYLES.map((style) => {
            const isSelected = selectedStyle === style.id;
            return (
              <button
                type="button"
                key={style.id}
                onClick={() => handleStyleSelect(style.id)}
                data-testid={`certificate-style-${style.id}`}
                className={`relative text-left p-3.5 rounded-xl border-2 transition-all flex flex-col justify-between gap-2.5 ${
                  isSelected
                    ? "border-eh-ink bg-eh-cream/30 shadow-sm"
                    : "border-eh-border-soft hover:border-eh-border bg-white"
                }`}
              >
                {style.preview}
                <div>
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-medium text-xs text-eh-ink">{t(style.titleKey)}</span>
                    {isSelected && (
                      <span className="w-4 h-4 rounded-full bg-eh-ink text-white flex items-center justify-center shrink-0">
                        <Check className="w-2.5 h-2.5" />
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-eh-muted mt-0.5 line-clamp-2">
                    {t(style.descKey)}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom Certificate Logo Upload */}
      <div className="space-y-2 pt-2 border-t border-eh-border-soft">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-semibold uppercase tracking-wider text-eh-muted">
            {t("brand.certificateLogoLabel")}
          </Label>
        </div>
        <p className="text-xs text-eh-muted">{t("brand.certificateLogoHint")}</p>

        <div className="mt-2 border-2 border-dashed border-eh-border-soft rounded-xl p-4 text-center hover:bg-eh-cream/30 transition">
          {customLogoUrl ? (
            <div className="space-y-2">
              <img
                src={customLogoUrl}
                alt=""
                className="max-h-20 mx-auto rounded object-contain"
              />
              <button
                type="button"
                onClick={() => setBranding({ ...branding, certificate_logo_url: "" })}
                data-testid="certificate-logo-remove"
                className="text-xs text-rose-500 hover:underline"
              >
                {t("brand.remove")}
              </button>
            </div>
          ) : (
            <label className="cursor-pointer block">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onUpload(e, "certificate_logo_url")}
                data-testid="upload-certificate_logo_url"
              />
              {brandLogoUrl ? (
                <div className="space-y-1">
                  <img
                    src={brandLogoUrl}
                    alt=""
                    className="max-h-12 mx-auto rounded opacity-60 object-contain"
                  />
                  <div className="text-xs text-eh-muted mt-1">{t("brand.clickUpload")}</div>
                </div>
              ) : (
                <>
                  <Upload className="w-5 h-5 mx-auto text-eh-muted mb-1.5" />
                  <div className="text-xs text-eh-muted">{t("brand.clickUpload")}</div>
                </>
              )}
            </label>
          )}
        </div>

        <Input
          value={customLogoUrl}
          onChange={(e) =>
            setBranding({ ...branding, certificate_logo_url: e.target.value })
          }
          placeholder={t("brand.orPasteUrl")}
          className="mt-2 text-xs"
        />
      </div>
    </div>
  );
}
